/**
 * Live time-clock store. All clock state is derived from the server on poll — actions return
 * the server response (callers read `state_coupling`) and then refetch `/active` (+ `/day`);
 * we never optimistically mutate the session list. A single app-wide 20 s poll of `/active`
 * drives the top-bar pill; surfaces tick a 1 s display clock locally (worktimeLib.useNow).
 */
import { useMemo } from 'react';
import axios from 'axios';
import { create } from 'zustand';

import { getOrganizationId } from './store';
import { todayStr, livePresenceSeconds } from './components/worktime/clock/worktimeLib';

const BASE = '/api/scheduling/timeclock';
const ACK_KEY = 'timeclockAckReviewIds';

function loadAck() {
  try {
    return new Set(JSON.parse(localStorage.getItem(ACK_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function persistAck(idSet) {
  try {
    localStorage.setItem(ACK_KEY, JSON.stringify([...idSet]));
  } catch {
    /* ignore quota / disabled storage */
  }
}

let pollTimer = null;

export const useTimeclockStore = create((set, get) => ({
  active: [], // running sessions for the caller (0..n)
  day: null, // { sessions, breakdown } for today
  config: null, // { track, allow_parallel, handling, ... }
  board: [], // all running sessions org-wide (manager board; foreman+)
  pending: [], // local dates (YYYY-MM-DD) the caller still needs to confirm
  loading: false,
  error: null,
  acknowledgedReviewIds: loadAck(), // P-watchdog dismissals (device-local; see PWatchdogDialog)

  fetchConfig: async () => {
    const orgId = getOrganizationId();
    if (!orgId) return null;
    try {
      const res = await axios.get(`${BASE}/config/?organization=${orgId}`);
      set({ config: res.data });
      return res.data;
    } catch {
      return null;
    }
  },

  fetchActive: async () => {
    const orgId = getOrganizationId();
    if (!orgId) return;
    try {
      const res = await axios.get(`${BASE}/active/?organization=${orgId}`);
      set({ active: Array.isArray(res.data) ? res.data : [], error: null });
    } catch {
      set({ active: [] });
    }
  },

  fetchDay: async (date) => {
    const orgId = getOrganizationId();
    if (!orgId) return null;
    try {
      const res = await axios.get(`${BASE}/day/${date || todayStr()}/?organization=${orgId}`);
      set({ day: res.data });
      return res.data;
    } catch {
      return null;
    }
  },

  fetchPending: async () => {
    const orgId = getOrganizationId();
    if (!orgId) return [];
    try {
      const res = await axios.get(`${BASE}/pending/?organization=${orgId}`);
      const dates = res.data?.dates || [];
      set({ pending: dates });
      return dates;
    } catch {
      set({ pending: [] });
      return [];
    }
  },

  refresh: async () => {
    await Promise.all([get().fetchActive(), get().fetchDay(), get().fetchPending()]);
  },

  clockIn: async ({ kind, maintenanceId }) => {
    const orgId = getOrganizationId();
    const body = { kind };
    if (maintenanceId) body.maintenance_id = maintenanceId;
    const res = await axios.post(`${BASE}/clock-in/?organization=${orgId}`, body);
    await get().refresh();
    return res.data; // includes state_coupling
  },

  clockOut: async (sessionId) => {
    const orgId = getOrganizationId();
    const res = await axios.post(`${BASE}/clock-out/?organization=${orgId}`, { session_id: sessionId });
    await get().refresh();
    return res.data;
  },

  switchWo: async (toMaintenanceId) => {
    const orgId = getOrganizationId();
    const res = await axios.post(`${BASE}/switch/?organization=${orgId}`, { to_maintenance_id: toMaintenanceId });
    await get().refresh();
    return res.data;
  },

  // --- manager board / reconcile / settings (reuse the same API layer) ---

  fetchBoard: async () => {
    const orgId = getOrganizationId();
    if (!orgId) return;
    try {
      const res = await axios.get(`${BASE}/board/?organization=${orgId}`);
      set({ board: Array.isArray(res.data) ? res.data : [] });
    } catch {
      /* foreman+ only; leave previous board */
    }
  },

  // Day for an arbitrary employee/date (reconcile) — returns data WITHOUT clobbering the
  // tablet's `day`. The reconcile screen holds the working set in component-local state.
  fetchDayFor: async (date, employeeId) => {
    const orgId = getOrganizationId();
    if (!orgId) return null;
    let url = `${BASE}/day/${date || todayStr()}/?organization=${orgId}`;
    if (employeeId) url += `&employee_id=${employeeId}`;
    const res = await axios.get(url);
    return res.data;
  },

  commitDay: async ({ date, employeeId }) => {
    const orgId = getOrganizationId();
    const body = { date: date || todayStr() };
    if (employeeId) body.employee_id = employeeId;
    const res = await axios.post(`${BASE}/commit/?organization=${orgId}`, body);
    return res.data; // { work_day, breakdown }
  },

  resolveSession: async (sessionId, { endedAt } = {}) => {
    const orgId = getOrganizationId();
    const body = {};
    if (endedAt) body.ended_at = endedAt;
    const res = await axios.post(`${BASE}/sessions/${sessionId}/resolve/?organization=${orgId}`, body);
    return res.data;
  },

  // Wraps the existing WorkDay nested-block CRUD (full-replace). Caller MUST send every block
  // it wants to keep (incl. the source=clock blocks, by id) — see the reconcile flow.
  saveWorkDayBlocks: async (workDayId, blocks) => {
    const orgId = getOrganizationId();
    const res = await axios.patch(`/api/scheduling/work-days/${workDayId}/?organization=${orgId}`, { blocks });
    return res.data;
  },

  updateConfig: async (patch) => {
    const orgId = getOrganizationId();
    const res = await axios.patch(`${BASE}/config/?organization=${orgId}`, patch);
    set({ config: res.data });
    return res.data;
  },

  acknowledgeReview: (sessionId) => {
    const next = new Set(get().acknowledgedReviewIds);
    next.add(sessionId);
    persistAck(next);
    set({ acknowledgedReviewIds: next });
  },

  startPolling: (intervalMs = 20000) => {
    if (pollTimer) return;
    get().fetchConfig();
    get().fetchActive();
    get().fetchDay(); // keep today's sessions/breakdown fresh app-wide so every clock surface agrees
    get().fetchPending();
    pollTimer = setInterval(() => {
      get().fetchActive();
      get().fetchDay();
      get().fetchPending();
    }, intervalMs);
  },

  stopPolling: () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  },
}));

// --- derived selectors (active only ever contains running sessions) ---
export const selectDaySession = (s) => s.active.find((x) => x.kind === 'day') || null;
export const selectWoSession = (s) => s.active.find((x) => x.kind === 'work_order') || null;
export const selectBreakSession = (s) => s.active.find((x) => x.kind === 'break') || null;
export const selectWoSessions = (s) => s.active.filter((x) => x.kind === 'work_order');
export const selectReviewSession = (s) =>
  s.active.find((x) => x.needs_review && !s.acknowledgedReviewIds.has(x.id)) || null;

const spanSeconds = (startIso, endIso) => Math.max(0, Math.round((new Date(endIso) - new Date(startIso)) / 1000));

/**
 * Today's cumulative presence (läsnäolo) in seconds — THE single source of truth for every clock
 * surface (sidebar pill, tablet, panels). Sum of every day session's span (closed ones by their
 * span, the running one ticking to `nowMs`); a fully clocked-out gap between sessions is absence,
 * so it is naturally excluded. Mirrors the backend §11 presence. Consume via `useDayPresenceSeconds`.
 */
export function computeDayPresenceSeconds(day, daySession, nowMs) {
  const daySessions = (day?.sessions || []).filter((s) => s.kind === 'day');
  // Base = sum of today's already-closed day spans (non-ticking).
  const base = daySessions
    .filter((s) => s.ended_at)
    .reduce((acc, s) => acc + spanSeconds(s.started_at, s.ended_at), 0);
  // Running day session: the one in /day, or `active`'s (which may not be in /day yet).
  const running = daySessions.find((s) => !s.ended_at) || daySession || null;
  // Same formula the board uses — base + the running session's live elapsed.
  return livePresenceSeconds(base, running?.started_at, nowMs);
}

/** Hook wrapper: cumulative day presence in seconds, ticking with `nowMs`. */
export function useDayPresenceSeconds(nowMs) {
  const day = useTimeclockStore((s) => s.day);
  const daySession = useTimeclockStore(selectDaySession);
  return useMemo(() => computeDayPresenceSeconds(day, daySession, nowMs), [day, daySession, nowMs]);
}
